/**
 * CadenceReporting — aggregate reporting for the Mi6 7-step cadence engine.
 *
 * Computes compliance, touches, overdue, and decision gate metrics
 * entirely client-side from cadence-store.ts (localStorage).
 *
 * Cut-by modes: BDR/AE · Account · Source · Cadence Step · Day
 */

import { useEffect, useMemo, useState } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { BackBar } from "@/components/BackBar";
import {
  loadProspects,
  loadActions,
  CADENCE_PROGRAM,
  type CadenceProspect,
  type CadenceAction,
  type ProspectSource,
  type CadenceStepId,
} from "@/lib/cadence-store";
import { seedDemoTeamDataIfEmpty } from "@/lib/reporting-calc";
import { getCurrentTenant } from "@/lib/session";

// ─── Palette ──────────────────────────────────────────────────────────────────

const MI6_BLACK = "#0B0B0F";
const MI6_RED  = "#DC2626";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function addDays(iso: string, n: number): string {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function daysAgo(n: number) {
  return addDays(todayISO(), -n);
}

type TimeRange = "today" | "this_week" | "last_7" | "this_month";
type CutBy = "rep" | "account" | "source" | "step" | "day";
type OwnerFilter = "all" | string;

function complianceColor(pct: number): string {
  if (pct >= 80) return "#16A34A";
  if (pct >= 50) return "#CA8A04";
  return "#DC2626";
}

function pct(done: number, total: number): number {
  return total === 0 ? 0 : Math.round((done / total) * 100);
}

// ─── Date range resolution ────────────────────────────────────────────────────

function resolveRange(range: TimeRange): { from: string; to: string } {
  const today = todayISO();
  if (range === "today")      return { from: today, to: today };
  if (range === "last_7")     return { from: daysAgo(6), to: today };
  if (range === "this_month") {
    const d = new Date();
    const from = new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
    return { from, to: today };
  }
  // this_week: Monday to today
  const d = new Date();
  const day = d.getDay() === 0 ? 6 : d.getDay() - 1; // Mon=0
  const mon = new Date(d);
  mon.setDate(d.getDate() - day);
  return { from: mon.toISOString().slice(0, 10), to: today };
}

function inRange(iso: string, from: string, to: string): boolean {
  const day = iso.slice(0, 10);
  return day >= from && day <= to;
}

// ─── Chip / pill helper ───────────────────────────────────────────────────────

function Chip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap"
      style={{
        background: active ? MI6_BLACK : "#EFEFEE",
        color: active ? "#FFFFFF" : "#525258",
        border: active ? `1px solid ${MI6_BLACK}` : "1px solid #E5E5E7",
      }}
    >
      {label}
    </button>
  );
}

// ─── Metric tile ──────────────────────────────────────────────────────────────

function MetricTile({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string | number;
  sub?: string;
  color?: string;
}) {
  return (
    <div
      className="bg-white rounded-xl p-5 shadow-sm border"
      style={{ borderColor: "#E5E5E7" }}
    >
      <div
        className="text-[9px] uppercase tracking-[0.2em] font-semibold mb-2"
        style={{ color: "#8E8E94" }}
      >
        {label}
      </div>
      <div
        className="text-3xl font-black tabular-nums leading-none"
        style={{ color: color ?? MI6_BLACK }}
      >
        {value}
      </div>
      {sub && (
        <div className="text-xs text-neutral-500 mt-1.5 leading-tight">{sub}</div>
      )}
    </div>
  );
}

// ─── Table wrapper ────────────────────────────────────────────────────────────

function DataTable({
  headers,
  children,
}: {
  headers: string[];
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-100">
            {headers.map((h) => (
              <th
                key={h}
                className="text-left px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-semibold"
                style={{ color: "#8E8E94" }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-50">{children}</tbody>
      </table>
    </div>
  );
}

function ComplianceBar({ pct: p }: { pct: number }) {
  return (
    <div className="flex items-center gap-2 min-w-[100px]">
      <div className="flex-1 h-1.5 rounded-full bg-neutral-100 overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${Math.min(p, 100)}%`, background: complianceColor(p) }}
        />
      </div>
      <span
        className="text-xs font-bold tabular-nums w-8 text-right"
        style={{ color: complianceColor(p) }}
      >
        {p}%
      </span>
    </div>
  );
}

// ─── Cut-by views ─────────────────────────────────────────────────────────────

interface ReportData {
  prospects: CadenceProspect[];
  actions: CadenceAction[];
  from: string;
  to: string;
  ownerFilter: OwnerFilter;
}

// By Rep
function ByRepTable({ prospects, actions, from, to, ownerFilter }: ReportData) {
  const owners = useMemo(() => {
    const map = new Map<string, { id: string; prospects: CadenceProspect[] }>();
    for (const p of prospects) {
      if (ownerFilter !== "all" && p.owner_id !== ownerFilter) continue;
      if (!map.has(p.owner_id)) {
        map.set(p.owner_id, { id: p.owner_id, prospects: [] });
      }
      map.get(p.owner_id)!.prospects.push(p);
    }
    return [...map.values()];
  }, [prospects, ownerFilter]);

  const rows = useMemo(() =>
    owners.map((o) => {
      const ids = new Set(o.prospects.map((p) => p.id));
      const repActions = actions.filter(
        (a) => ids.has(a.prospect_id) && inRange(a.executed_at, from, to)
      );
      const active = o.prospects.filter((p) => p.status === "active");
      const overdue = active.filter(
        (p) => p.next_action_at < todayISO() && p.current_step < 7
      ).length;
      const atGate = active.filter((p) => p.current_step === 7).length;
      const due = active.filter((p) => p.next_action_at >= from && p.next_action_at <= to).length;
      const done = repActions.length;
      return {
        id: o.id,
        totalDue: due,
        totalDone: done,
        compliance: pct(done, due),
        overdue,
        atGate,
      };
    }),
    [owners, actions, from, to]
  );

  return (
    <DataTable headers={["Rep / Owner", "Due", "Done", "Compliance", "Overdue", "At Pursuit Decision"]}>
      {rows.map((r) => (
        <tr key={r.id} className="hover:bg-neutral-50/60 transition-colors">
          <td className="px-4 py-2.5 font-medium text-neutral-800 font-mono text-xs">{r.id}</td>
          <td className="px-4 py-2.5 tabular-nums text-neutral-600">{r.totalDue}</td>
          <td className="px-4 py-2.5 tabular-nums text-neutral-600">{r.totalDone}</td>
          <td className="px-4 py-2.5"><ComplianceBar pct={r.compliance} /></td>
          <td className="px-4 py-2.5 tabular-nums font-semibold" style={{ color: r.overdue > 0 ? MI6_RED : "#6B6B70" }}>{r.overdue}</td>
          <td className="px-4 py-2.5 tabular-nums text-neutral-600">{r.atGate}</td>
        </tr>
      ))}
      {rows.length === 0 && (
        <tr><td colSpan={6} className="px-4 py-6 text-center text-neutral-500 text-sm">No data for selected filters</td></tr>
      )}
    </DataTable>
  );
}

// By Account
function ByAccountTable({ prospects, ownerFilter }: ReportData) {
  const today = todayISO();
  const filtered = prospects.filter(
    (p) => p.status === "active" && (ownerFilter === "all" || p.owner_id === ownerFilter)
  );

  return (
    <DataTable headers={["Name", "Company", "Step", "Days in Cadence", "Last Action", "Next Action", "Status"]}>
      {filtered.map((p) => {
        const enrolled = new Date(p.enrolled_at + "T00:00:00");
        const days = Math.floor((Date.now() - enrolled.getTime()) / 86400000);
        const isOverdue = p.next_action_at < today && p.current_step < 7;
        const step = CADENCE_PROGRAM.find((s) => s.id === p.current_step);
        return (
          <tr key={p.id} className="hover:bg-neutral-50/60 transition-colors">
            <td className="px-4 py-2.5 font-medium text-neutral-800">{p.full_name}</td>
            <td className="px-4 py-2.5 text-neutral-600 text-xs">{p.company}</td>
            <td className="px-4 py-2.5">
              <span className="text-[10px] px-2 py-0.5 rounded font-semibold"
                style={{ background: `${MI6_BLACK}12`, color: MI6_BLACK }}>
                Step {p.current_step} — {step?.short ?? ""}
              </span>
            </td>
            <td className="px-4 py-2.5 tabular-nums text-neutral-500">{days}d</td>
            <td className="px-4 py-2.5 text-xs text-neutral-500">
              {p.last_action_at ? p.last_action_at.slice(0, 10) : "—"}
            </td>
            <td className="px-4 py-2.5 text-xs" style={{ color: isOverdue ? MI6_RED : "#6B6B70" }}>
              {p.next_action_at}
            </td>
            <td className="px-4 py-2.5">
              {isOverdue ? (
                <span className="text-[10px] px-2 py-0.5 rounded font-semibold bg-red-50 border border-red-100" style={{ color: MI6_RED }}>Overdue</span>
              ) : p.current_step === 7 ? (
                <span className="text-[10px] px-2 py-0.5 rounded font-semibold" style={{ background: "#FEF3C7", color: "#92400E" }}>At Decision</span>
              ) : (
                <span className="text-[10px] px-2 py-0.5 rounded font-semibold" style={{ background: "#F0FDF4", color: "#166534" }}>On track</span>
              )}
            </td>
          </tr>
        );
      })}
      {filtered.length === 0 && (
        <tr><td colSpan={7} className="px-4 py-6 text-center text-neutral-500 text-sm">No active prospects for selected filters</td></tr>
      )}
    </DataTable>
  );
}

// By Source
const SOURCE_LABELS: Record<ProspectSource, string> = {
  marketing_mql:    "Marketing MQL",
  cold_outbound:    "Cold Outbound",
  event:            "Event",
  referral:         "Referral",
  manager_assigned: "Manager Assigned",
};

function BySourceTable({ prospects, ownerFilter }: ReportData) {
  const sources: ProspectSource[] = ["marketing_mql", "cold_outbound", "event", "referral", "manager_assigned"];

  const rows = useMemo(() =>
    sources.map((src) => {
      const ps = prospects.filter(
        (p) => p.source === src && (ownerFilter === "all" || p.owner_id === ownerFilter)
      );
      const pastStep3 = ps.filter((p) => p.current_step > 3 || p.status !== "active").length;
      const atGate = ps.filter((p) => p.current_step === 7 || p.status === "marketing_hook").length;
      const converted = ps.filter((p) => p.status === "converted").length;
      return {
        src,
        label: SOURCE_LABELS[src],
        volume: ps.length,
        pctPastStep3: pct(pastStep3, ps.length),
        pctAtGate: pct(atGate, ps.length),
        pctConverted: pct(converted, ps.length),
      };
    }),
    [prospects, ownerFilter, sources]
  );

  return (
    <DataTable headers={["Source", "Volume", "Past Step 3", "At Pursuit Decision", "Converted to AE"]}>
      {rows.map((r) => (
        <tr key={r.src} className="hover:bg-neutral-50/60 transition-colors">
          <td className="px-4 py-2.5 font-medium text-neutral-800">{r.label}</td>
          <td className="px-4 py-2.5 tabular-nums text-neutral-600">{r.volume}</td>
          <td className="px-4 py-2.5"><ComplianceBar pct={r.pctPastStep3} /></td>
          <td className="px-4 py-2.5"><ComplianceBar pct={r.pctAtGate} /></td>
          <td className="px-4 py-2.5"><ComplianceBar pct={r.pctConverted} /></td>
        </tr>
      ))}
    </DataTable>
  );
}

// By Step
function ByStepTable({ prospects, actions, from, to, ownerFilter }: ReportData) {
  const rows = useMemo(() =>
    CADENCE_PROGRAM.map((def) => {
      const filtered = prospects.filter(
        (p) => ownerFilter === "all" || p.owner_id === ownerFilter
      );
      const dueToday = filtered.filter(
        (p) => p.current_step === def.id && p.status === "active" && p.next_action_at >= from && p.next_action_at <= to
      ).length;
      const done = actions.filter(
        (a) => a.step === def.id && inRange(a.executed_at, from, to) &&
          (ownerFilter === "all" || a.owner_id === ownerFilter)
      ).length;
      // Avg time-in-step: rough approximation from enrolled_at + day_offset vs last_action_at
      const completedProspects = filtered.filter(
        (p) => p.current_step > def.id && p.last_action_at
      );
      const avgDays = completedProspects.length > 0
        ? Math.round(
            completedProspects.reduce((s, p) => {
              const nextDef = CADENCE_PROGRAM.find((d) => d.id === (def.id + 1) as CadenceStepId);
              if (!nextDef) return s;
              return s + (nextDef.day_offset - def.day_offset);
            }, 0) / completedProspects.length
          )
        : 0;

      return { def, dueToday, done, compliance: pct(done, dueToday), avgDays };
    }),
    [prospects, actions, from, to, ownerFilter]
  );

  return (
    <DataTable headers={["Step", "Due in Range", "Done in Range", "Compliance", "Avg Days in Step"]}>
      {rows.map((r) => (
        <tr key={r.def.id} className="hover:bg-neutral-50/60 transition-colors">
          <td className="px-4 py-2.5">
            <div className="font-semibold text-neutral-800 text-sm">Step {r.def.id}</div>
            <div className="text-[11px] text-neutral-500">{r.def.label}</div>
          </td>
          <td className="px-4 py-2.5 tabular-nums text-neutral-600">{r.dueToday}</td>
          <td className="px-4 py-2.5 tabular-nums text-neutral-600">{r.done}</td>
          <td className="px-4 py-2.5"><ComplianceBar pct={r.compliance} /></td>
          <td className="px-4 py-2.5 tabular-nums text-neutral-500">{r.avgDays}d</td>
        </tr>
      ))}
    </DataTable>
  );
}

// By Day (last 14 days heatmap)
function ByDayTable({ prospects, actions, ownerFilter }: ReportData) {
  const days = useMemo(() => {
    const result: Array<{ date: string; due: number; done: number; compliance: number }> = [];
    for (let i = 13; i >= 0; i--) {
      const date = daysAgo(i);
      const duePs = prospects.filter(
        (p) => (ownerFilter === "all" || p.owner_id === ownerFilter) &&
          p.status === "active" && p.next_action_at === date
      ).length;
      const doneActs = actions.filter(
        (a) => (ownerFilter === "all" || a.owner_id === ownerFilter) &&
          a.executed_at.slice(0, 10) === date
      ).length;
      result.push({ date, due: duePs, done: doneActs, compliance: pct(doneActs, duePs) });
    }
    return result;
  }, [prospects, actions, ownerFilter]);

  return (
    <div>
      <div className="grid grid-cols-7 gap-2 mb-3">
        {days.map((d) => {
          const c = d.due === 0 ? "#EFEFEE" : complianceColor(d.compliance);
          const opacity = d.due === 0 ? 0.3 : 0.15 + (d.compliance / 100) * 0.85;
          const isToday = d.date === todayISO();
          return (
            <div
              key={d.date}
              title={`${d.date}: ${d.done} done of ${d.due} due (${d.compliance}%)`}
              className="rounded-lg p-2 flex flex-col items-center gap-0.5 cursor-default"
              style={{
                background: c,
                opacity: d.due === 0 ? 0.4 : opacity,
                border: isToday ? `2px solid ${MI6_BLACK}` : "2px solid transparent",
              }}
            >
              <div className="text-[10px] font-bold" style={{ color: d.due === 0 ? "#8E8E94" : "#0B0B0F" }}>
                {d.date.slice(5)}
              </div>
              {d.due > 0 && (
                <div className="text-[9px] font-semibold" style={{ color: complianceColor(d.compliance) }}>
                  {d.compliance}%
                </div>
              )}
            </div>
          );
        })}
      </div>
      <DataTable headers={["Date", "Due", "Done", "Compliance"]}>
        {days.map((d) => (
          <tr key={d.date} className="hover:bg-neutral-50/60 transition-colors">
            <td className="px-4 py-2.5 font-medium text-neutral-700 tabular-nums">
              {d.date === todayISO() ? `${d.date} (today)` : d.date}
            </td>
            <td className="px-4 py-2.5 tabular-nums text-neutral-600">{d.due}</td>
            <td className="px-4 py-2.5 tabular-nums text-neutral-600">{d.done}</td>
            <td className="px-4 py-2.5">
              {d.due > 0 ? <ComplianceBar pct={d.compliance} /> : <span className="text-xs text-neutral-300">—</span>}
            </td>
          </tr>
        ))}
      </DataTable>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function CadenceReporting() {
  const [timeRange, setTimeRange] = useState<TimeRange>("today");
  const [cutBy, setCutBy]         = useState<CutBy>("rep");
  const [ownerFilter, setOwnerFilter] = useState<OwnerFilter>("all");

  useEffect(() => {
    // Only seed sample team reporting data for demo tenants.
    const tenant = getCurrentTenant();
    if (tenant?.is_demo === true) {
      seedDemoTeamDataIfEmpty();
    }
  }, []);

  const prospects = useMemo(() => loadProspects(), []);
  const actions   = useMemo(() => loadActions(), []);
  const today     = todayISO();

  const { from, to } = useMemo(() => resolveRange(timeRange), [timeRange]);

  // Unique owner IDs for the owner filter dropdown
  const ownerIds = useMemo(() => {
    const ids = [...new Set(prospects.map((p) => p.owner_id))];
    return ids.sort();
  }, [prospects]);

  // ── Top-level metrics ──────────────────────────────────────────────────────
  const filteredProspects = useMemo(
    () => prospects.filter((p) => ownerFilter === "all" || p.owner_id === ownerFilter),
    [prospects, ownerFilter]
  );
  const filteredActions = useMemo(
    () => actions.filter((a) => ownerFilter === "all" || a.owner_id === ownerFilter),
    [actions, ownerFilter]
  );

  const activeProspects = filteredProspects.filter((p) => p.status === "active");

  // Compliance: due in range vs. actions executed in range
  const dueInRange = activeProspects.filter(
    (p) => p.next_action_at >= from && p.next_action_at <= to
  ).length;
  const actionsInRange = filteredActions.filter(
    (a) => inRange(a.executed_at, from, to)
  ).length;
  const compliancePct = pct(actionsInRange, dueInRange);

  // Overdue (snapshot, not time-range-filtered)
  const overdueCount = activeProspects.filter(
    (p) => p.next_action_at < today && p.current_step < 7
  ).length;

  // Decision gates
  const gateCount = filteredProspects.filter(
    (p) => p.current_step === 7 || p.status === "marketing_hook"
  ).length;
  const hookCount = filteredProspects.filter((p) => p.status === "marketing_hook").length;
  const parkCount = filteredProspects.filter((p) => p.status === "paused").length;
  const dqCount   = filteredProspects.filter((p) => p.status === "disqualified").length;

  const reportData: ReportData = {
    prospects: filteredProspects,
    actions: filteredActions,
    from,
    to,
    ownerFilter,
  };

  return (
    <OperatorShell breadcrumbs={[{ label: "Cadence Reporting" }]} pageTitle="Cadence Reporting" pageMeta="Aggregate cadence data">
      <BackBar href="/cadence/bdr" label="Cadence Queue" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6">

        {/* ── Header ───────────────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div
              className="text-[10px] uppercase tracking-[0.28em] font-bold mb-1.5"
              style={{ color: MI6_RED }}
            >
              Cadence Engine
            </div>
            <h1 className="text-2xl font-bold" style={{ color: MI6_BLACK }}>
              Cadence Reporting
            </h1>
          </div>
        </div>

        {/* ── Filter row ───────────────────────────────────────────────────── */}
        <div
          className="bg-white rounded-xl p-4 shadow-sm border flex flex-wrap gap-4 items-center"
          style={{ borderColor: "#E5E5E7" }}
        >
          {/* Time range */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500 mr-1">Range</span>
            {(["today", "this_week", "last_7", "this_month"] as TimeRange[]).map((r) => (
              <Chip
                key={r}
                label={r === "today" ? "Today" : r === "this_week" ? "This Week" : r === "last_7" ? "Last 7 Days" : "This Month"}
                active={timeRange === r}
                onClick={() => setTimeRange(r)}
              />
            ))}
          </div>

          <div className="w-px h-6 bg-neutral-200 hidden sm:block" />

          {/* Cut by */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500 mr-1">Cut by</span>
            {(["rep", "account", "source", "step", "day"] as CutBy[]).map((c) => (
              <Chip
                key={c}
                label={c === "rep" ? "BDR / AE" : c.charAt(0).toUpperCase() + c.slice(1)}
                active={cutBy === c}
                onClick={() => setCutBy(c)}
              />
            ))}
          </div>

          <div className="w-px h-6 bg-neutral-200 hidden sm:block" />

          {/* Owner filter */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500 mr-1">Owner</span>
            <select
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.target.value)}
              className="text-xs border rounded-lg px-2.5 py-1.5 bg-white font-medium"
              style={{ borderColor: "#E5E5E7", color: MI6_BLACK }}
            >
              <option value="all">All</option>
              {ownerIds.map((id) => (
                <option key={id} value={id}>{id}</option>
              ))}
            </select>
          </div>
        </div>

        {/* ── Top metric tiles ─────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricTile
            label="Cadence Compliance"
            value={`${compliancePct}%`}
            sub={`${actionsInRange} of ${dueInRange} completed in range`}
            color={complianceColor(compliancePct)}
          />
          <MetricTile
            label="Total Touches Executed"
            value={actionsInRange}
            sub={`in ${from === to ? from : `${from} — ${to}`}`}
          />
          <MetricTile
            label="Overdue Count"
            value={overdueCount}
            sub="Current snapshot — active prospects past due"
            color={overdueCount > 0 ? MI6_RED : "#16A34A"}
          />
          <MetricTile
            label="Pursuit Decisions Resolved"
            value={gateCount}
            sub={`Hook ${hookCount} · Park ${parkCount} · DQ ${dqCount}`}
            color="#CA8A04"
          />
        </div>

        {/* ── Main table panel ──────────────────────────────────────────────── */}
        <div
          className="bg-white rounded-xl shadow-sm border overflow-hidden"
          style={{ borderColor: "#E5E5E7" }}
        >
          <div
            className="px-5 py-4 border-b flex items-center justify-between"
            style={{ borderColor: "#EFEFEE" }}
          >
            <div className="font-semibold text-neutral-800 text-sm">
              {cutBy === "rep"     && "Results by AE"}
              {cutBy === "account" && "Results by Account"}
              {cutBy === "source"  && "Results by Source"}
              {cutBy === "step"    && "Results by Cadence Step"}
              {cutBy === "day"     && "Daily Compliance — Last 14 Days"}
            </div>
            <div className="text-[10px] text-neutral-500 uppercase tracking-widest">
              {ownerFilter === "all" ? "All owners" : ownerFilter}
            </div>
          </div>

          <div className="p-1">
            {cutBy === "rep"     && <ByRepTable    {...reportData} />}
            {cutBy === "account" && <ByAccountTable {...reportData} />}
            {cutBy === "source"  && <BySourceTable  {...reportData} />}
            {cutBy === "step"    && <ByStepTable    {...reportData} />}
            {cutBy === "day"     && (
              <div className="p-4">
                <ByDayTable {...reportData} />
              </div>
            )}
          </div>
        </div>

      </div>
    </OperatorShell>
  );
}
